import {
  StatsigOptions,
  StatsigProvider as BaseStatsigProvider,
} from "@statsig/react-bindings";
import { StatsigClient } from "@statsig/js-client";
import { getEnvFromHostname } from "@growtherapy/analytics";
import { getCurrentHost, withRetry } from "../utils";
import { datadogRum } from "@datadog/browser-rum";
import { createContext } from "react";
import { ChromeToolbarPlaybackControlsSingleton } from "../classes/ChromeToolbarPlaybackControlsSingleton";
import { logger } from "../datadog/logger";
import { LoadingScreen } from "../components/LoadingScreen";
import { useAsync } from "react-use";
import { secondsToMilliseconds } from "date-fns";

export const StatsigLoadingContext = createContext<boolean>(false);
type StatsigProviderOptions = StatsigOptions & {
  gateEvaluationCallback?: (key: string, value: boolean) => void;
  api?: string;
};

const statsigOptions: StatsigProviderOptions = {
  environment: {
    tier: getEnvFromHostname({ hostname: getCurrentHost() }),
  },
  // https://docs.datadoghq.com/real_user_monitoring/guide/setup-feature-flag-data-collection/?tab=browser#statsig-integration
  gateEvaluationCallback(key: string, value: boolean) {
    datadogRum.addFeatureFlagEvaluation(key, value);
  },
  // https://docs.statsig.com/infrastructure/managed-proxy/
  api: "https://yjxpzuzkp2367lnlk2xoxcc56q0hrtww.lambda-url.ap-south-1.on.aws/v1",
};

const STATSIG_CLIENT_KEY = "client-y2YXcN60n7VU4EkzyZABzzKCDe6gCuQtb70uLgf3U1u";
const STATSIG_CLIENT_TIMEOUT_MS = secondsToMilliseconds(10);

export const Statsig = new StatsigClient(
  STATSIG_CLIENT_KEY,
  {},
  statsigOptions,
);

export type StatsigProviderProps = {
  children: React.ReactNode;
};

/**
 * Callback function to be executed after Statsig client initialization is
 * successful.
 *
 * Useful for initializing non-React things that depend on Statsig values.
 */
function onStatsigInitializationSuccess() {
  ChromeToolbarPlaybackControlsSingleton.getInstance();
}

const STATSIG_CLIENT_RETRIES = 3;
const STATSIG_CLIENT_INITIALIZATION_DELAY_MS = 500;
const STATSIG_CLIENT_INITIALIZATION_BACKOFF_FACTOR = 2;

async function initializeStatsigClient() {
  const details = await Statsig.initializeAsync({
    timeoutMs: STATSIG_CLIENT_TIMEOUT_MS,
    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/API/fetch#priority
     */
    priority: "high",
  });

  if (!details.success) {
    throw (
      details.error ?? new Error("Unsuccessful Statsig client initialization")
    );
  }

  return details;
}

const handleStatsigClientInitializationWithRetry = withRetry(
  initializeStatsigClient,
  {
    retries: STATSIG_CLIENT_RETRIES,
    initialDelayMs: STATSIG_CLIENT_INITIALIZATION_DELAY_MS,
    backoffFactor: STATSIG_CLIENT_INITIALIZATION_BACKOFF_FACTOR,
    onFailure: (args) => {
      logger.warn("Unsuccessful Statsig client initialization", args);
    },
  },
);

async function handleStatsigClientInitialization() {
  try {
    await handleStatsigClientInitializationWithRetry();
    onStatsigInitializationSuccess();
  } catch (error) {
    logger.error("Initialize Statsig client error", {}, error as Error);
  }
}

/**
 * Statsig Provider for the application. Initializes the {@link Statsig} client.
 */
export function StatsigProvider({ children }: StatsigProviderProps) {
  const { loading } = useAsync(handleStatsigClientInitialization, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return <BaseStatsigProvider client={Statsig}>{children}</BaseStatsigProvider>;
}
